/**
 * BoardNotifyAllSettingsModal — 워크스페이스 전체 게시판 알림 통합 관리
 *
 * 좌측: 워크스페이스 내 보드 목록 (선택)
 * 우측: 선택한 보드의 토글
 *   1) 알림 마스터 ON/OFF
 *   2) (마스터 ON 일 때) 댓글 알림 / 새 글 알림
 *
 * 기본값: 마스터 ON, 댓글 ON, 새 글 OFF
 * 워크스페이스/채널 게시판 대시보드 헤더의 알림 버튼에서 진입.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Trans, t } from '@lingui/macro';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { Bell, BellOff, MessageSquare, FileText, BookOpen, ListTodo, PenLine, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from '../../components/ui/use-toast';
import {
  fetchBoards,
  getBoardNotifyPrefsMap,
  setBoardNotifyPrefs,
  DEFAULT_BOARD_NOTIFY_PREFS,
  BoardNotifyPrefs,
} from '../../services/BoardService';
import { Board, BoardType } from '../../models/Board';
import { My } from '../../services/My';

interface Props {
  open: boolean;
  onClose: () => void;
  workspaceId: string | null;
  /** 채널 모드 — 이 보드들만 일괄 설정 (없으면 워크스페이스 전체) */
  boardIds?: string[];
  /** 보드를 직접 전달 (fetchBoards 스킵) — 태스크처럼 단일 보드 알림 설정용. 1개면 좌측 목록 숨김. */
  directBoards?: Board[];
}

function boardIcon(type: BoardType) {
  switch (type) {
    case 'notice': return Bell;
    case 'qna':    return MessageSquare;
    case 'wiki':   return BookOpen;
    case 'issue':  return ListTodo;
    case 'tasks':  return ListTodo;
    default:       return FileText;
  }
}

function typeLabel(type: BoardType): string {
  switch (type) {
    case 'notice': return t`Notice`;
    case 'qna':    return 'Q&A';
    case 'wiki':   return t`Wiki`;
    case 'issue':  return t`Issue`;
    case 'tasks':  return t`Task`;
    default:       return '';
  }
}

export const BoardNotifyAllSettingsModal: React.FC<Props> = ({ open, onClose, workspaceId, boardIds, directBoards }) => {
  const [boards, setBoards] = useState<Board[]>([]);
  const [prefsMap, setPrefsMap] = useState<Record<string, BoardNotifyPrefs>>({});
  const [selectedBoardId, setSelectedBoardId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);

  // 모달 열릴 때 보드 목록 + prefs 로드
  useEffect(() => {
    if (!open || !workspaceId || !My.user?.id) return;
    setLoading(true);
    (async () => {
      try {
        const list = directBoards ?? (boardIds
          ? (await fetchBoards(workspaceId)).filter(b => boardIds.includes(b.id))
          : await fetchBoards(workspaceId));
        setBoards(list);
        const ids = list.map(b => b.id);
        const map = await getBoardNotifyPrefsMap(ids, My.user!.id);
        setPrefsMap(map);
        // 기본 선택 — 첫 번째 보드
        if (list.length > 0) setSelectedBoardId(prev => prev && list.find(b => b.id === prev) ? prev : list[0].id);
      } catch (err) {
        console.error('[BoardNotifyAll] 로드 실패:', err);
        toast({ title: t`Failed to load notification settings`, variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    })();
  }, [open, workspaceId, boardIds, directBoards]);

  const selectedBoard = useMemo(
    () => boards.find(b => b.id === selectedBoardId) ?? null,
    [boards, selectedBoardId],
  );
  const selectedPrefs: BoardNotifyPrefs =
    (selectedBoardId && prefsMap[selectedBoardId]) || DEFAULT_BOARD_NOTIFY_PREFS;

  const updatePref = async (key: keyof BoardNotifyPrefs, value: boolean) => {
    if (!selectedBoardId || !My.user?.id) return;
    // optimistic
    setPrefsMap(prev => ({
      ...prev,
      [selectedBoardId]: { ...(prev[selectedBoardId] ?? DEFAULT_BOARD_NOTIFY_PREFS), [key]: value },
    }));
    setSavingId(selectedBoardId);
    try {
      await setBoardNotifyPrefs(selectedBoardId, My.user.id, { [key]: value });
    } catch (err) {
      console.error('[BoardNotifyAll] 저장 실패:', err);
      toast({ title: t`Failed to save settings`, variant: 'destructive' });
      // rollback
      setPrefsMap(prev => ({
        ...prev,
        [selectedBoardId]: { ...(prev[selectedBoardId] ?? DEFAULT_BOARD_NOTIFY_PREFS), [key]: !value },
      }));
    } finally {
      setSavingId(null);
    }
  };

  /** 보드 행의 우측 요약 인디케이터 — 알림 끔 / 댓글만 / 새글만 / 모두 등 */
  const summarize = (p: BoardNotifyPrefs): { label: string; tone: 'muted' | 'primary' | 'off' } => {
    if (!p.notifications_enabled) return { label: t`Off`, tone: 'off' };
    if (!p.notify_comment && !p.notify_new_post) return { label: t`Mentions only`, tone: 'primary' };
    if (p.notify_comment && p.notify_new_post)   return { label: t`All`, tone: 'primary' };
    if (p.notify_comment)                         return { label: t`Comments`, tone: 'primary' };
    return { label: t`New posts`, tone: 'primary' };
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-[820px] w-[95vw] h-[80vh] p-0 overflow-hidden flex flex-col">
        <DialogHeader className="px-5 py-3 border-b border-border shrink-0">
          <DialogTitle className="flex items-center gap-2 text-body font-semibold text-foreground">
            <Bell className="size-[18px] text-primary" />
            <Trans>Board notifications</Trans>
          </DialogTitle>
          <DialogDescription className="sr-only">
            <Trans>Manage per-board notification settings in one place. Select a board on the left and toggle types on the right.</Trans>
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 flex flex-col sm:flex-row overflow-hidden">
          {/* ── 좌측: 보드 목록 ── */}
          <aside className={cn("w-full max-h-[38vh] border-b shrink-0 sm:w-[260px] sm:max-h-none sm:border-b-0 sm:border-r border-border bg-muted/20 overflow-y-auto", boards.length <= 1 && "hidden")}>
            {loading ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                <Loader2 className="size-5 animate-spin" />
              </div>
            ) : boards.length === 0 ? (
              <p className="px-4 py-8 text-meta-sm text-muted-foreground text-center">
                <Trans>No boards in this workspace.</Trans>
              </p>
            ) : (
              <nav className="p-2 space-y-0.5">
                {boards.map(b => {
                  const Icon = boardIcon(b.type as BoardType);
                  const p = prefsMap[b.id] ?? DEFAULT_BOARD_NOTIFY_PREFS;
                  const sum = summarize(p);
                  const isActive = b.id === selectedBoardId;
                  return (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => setSelectedBoardId(b.id)}
                      data-state={isActive ? 'on' : undefined}
                      className={cn(
                        'w-full flex items-center gap-2 px-2 py-2 rounded-lg text-left transition-colors cursor-pointer',
                        isActive
                          ? 'bg-primary/10 text-primary'
                          : 'text-foreground hover:bg-muted',
                      )}
                    >
                      <Icon className={cn('size-3.5 shrink-0', isActive ? 'text-primary' : 'text-muted-foreground')} />
                      <span className="flex-1 truncate text-label font-medium">{b.name}</span>
                      <span className={cn(
                        'text-meta-sm shrink-0 px-1.5 rounded-full',
                        sum.tone === 'off'     && 'text-muted-foreground/70 bg-muted',
                        sum.tone === 'muted'   && 'text-muted-foreground bg-muted',
                        sum.tone === 'primary' && 'text-primary bg-primary/10',
                      )}>
                        {sum.label}
                      </span>
                    </button>
                  );
                })}
              </nav>
            )}
          </aside>

          {/* ── 우측: 선택된 보드 토글 ── */}
          <section className="flex-1 min-w-0 overflow-y-auto p-4 sm:p-6">
            {!selectedBoard ? (
              <p className="text-muted-foreground text-center py-12"><Trans>Select a board on the left.</Trans></p>
            ) : (
              <div className="space-y-6 max-w-[420px] mx-auto">
                {/* 보드 헤더 */}
                <div className="flex items-center gap-2.5 pb-3 border-b border-border">
                  {(() => {
                    const Icon = boardIcon(selectedBoard.type as BoardType);
                    return <Icon className="size-5 text-primary shrink-0" />;
                  })()}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-body font-semibold text-foreground truncate">{selectedBoard.name}</h3>
                    {typeLabel(selectedBoard.type as BoardType) && (
                      <p className="text-meta-sm text-muted-foreground mt-0.5">{t`${typeLabel(selectedBoard.type as BoardType)} board`}</p>
                    )}
                  </div>
                  {savingId === selectedBoard.id && (
                    <Loader2 className="size-4 animate-spin text-muted-foreground" />
                  )}
                </div>

                {/* 마스터 토글 */}
                <div className="flex items-start gap-3 p-3 rounded-lg border border-border bg-card">
                  <div className="size-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    {selectedPrefs.notifications_enabled ? <Bell className="size-4" /> : <BellOff className="size-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <span className="flex-1 text-label font-semibold text-foreground"><Trans>Notifications</Trans></span>
                      <Switch
                        checked={selectedPrefs.notifications_enabled}
                        onCheckedChange={v => updatePref('notifications_enabled', v)}
                      />
                    </label>
                    <p className="text-meta-sm text-muted-foreground mt-1 leading-relaxed">
                      <Trans>Turn all notifications for this board on or off. When off, you receive nothing. When on, you're always notified of mentions — even if both toggles below are off.</Trans>
                    </p>
                  </div>
                </div>

                {/* 세부 토글 — 마스터 ON 일 때만 활성 */}
                <div className={cn(
                  'space-y-2',
                  !selectedPrefs.notifications_enabled && 'opacity-50 pointer-events-none select-none',
                )}>
                  {/* 댓글 알림 */}
                  <div className="flex items-start gap-3 p-3 rounded-lg border border-border bg-background">
                    <div className="size-9 rounded-lg bg-muted text-muted-foreground flex items-center justify-center shrink-0">
                      <MessageSquare className="size-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <span className="flex-1 text-label font-medium text-foreground"><Trans>Comment notifications</Trans></span>
                        <Switch
                          checked={selectedPrefs.notify_comment}
                          onCheckedChange={v => updatePref('notify_comment', v)}
                          disabled={!selectedPrefs.notifications_enabled}
                        />
                      </label>
                      <p className="text-meta-sm text-muted-foreground mt-1 leading-relaxed">
                        <Trans>Notify when new comments are added to posts I've authored or participated in.</Trans>
                      </p>
                    </div>
                  </div>

                  {/* 새 글 알림 */}
                  <div className="flex items-start gap-3 p-3 rounded-lg border border-border bg-background">
                    <div className="size-9 rounded-lg bg-muted text-muted-foreground flex items-center justify-center shrink-0">
                      <PenLine className="size-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <span className="flex-1 text-label font-medium text-foreground"><Trans>New post notifications</Trans></span>
                        <Switch
                          checked={selectedPrefs.notify_new_post}
                          onCheckedChange={v => updatePref('notify_new_post', v)}
                          disabled={!selectedPrefs.notifications_enabled}
                        />
                      </label>
                      <p className="text-meta-sm text-muted-foreground mt-1 leading-relaxed">
                        <Trans>Notify when new posts are added to this board.</Trans>
                      </p>
                    </div>
                  </div>
                </div>

                <p className="text-meta-sm text-muted-foreground leading-relaxed">
                  <Trans>💡 Default: <strong>Notifications on · Comments on · New posts off</strong>. Changes save instantly.</Trans>
                </p>
              </div>
            )}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BoardNotifyAllSettingsModal;
